// Todo lo que decide la capa de verificación, sin React
// (ADR-2026-09-07-verificacion-de-calidad-en-la-columna).
import type { Accion, AnalisisImagen, ItemVerificacion, ModalidadVerificacion } from './tipos';

/** fidelidad ≤ 2 ⇒ mala (misma regla que `decision.ts` del backend). */
export const UMBRAL_MALAS = 2;
/** Con esta cantidad de malas visibles se ofrece "Regenerar las N imágenes malas". */
export const MIN_PARA_LOTE = 2;
/** Pasado esto sin fila, la card dice "Tardó demasiado" (Flash tarda 10–30 s; lo que se cubre es el worker caído). */
export const TOPE_ANALISIS_MS = 3 * 60_000;
/** Tope del body de `/listar`. */
export const LOTE_LISTAR = 60;

export const COLOR_POR_ACCION: Record<Accion, string> = {
  aceptar: '#30A46C',
  regenerar: '#E5484D',
  pedir_fotos: '#F5A524',
};
export const COLOR_SIN_DECISION = '#9CA3AF';

export type EstadoCard =
  | { tipo: 'oculto' }
  | { tipo: 'boton' }
  | { tipo: 'analizando' }
  | { tipo: 'lista'; analisis: AnalisisImagen }
  | { tipo: 'error'; motivo: string; analisis: AnalisisImagen | null };

/**
 * Qué se dibuja sobre la card. El error local (red, timeout) gana porque es lo último
 * que pasó; después la espera local (recién se tocó el botón y el backend todavía no
 * lo refleja); después lo que dice el backend.
 */
export function estadoDeCard(p: {
  item: ItemVerificacion | undefined;
  pendienteDesde: number | undefined;
  errorLocal: string | undefined;
  ahora: number;
}): EstadoCard {
  if (!p.item || !p.item.analizable) return { tipo: 'oculto' };
  if (p.errorLocal) return { tipo: 'error', motivo: p.errorLocal, analisis: p.item.analisis };
  if (p.pendienteDesde !== undefined) {
    if (p.ahora - p.pendienteDesde > TOPE_ANALISIS_MS) {
      return { tipo: 'error', motivo: 'timeout', analisis: p.item.analisis };
    }
    return { tipo: 'analizando' };
  }
  const a = p.item.analisis;
  if (!a) return { tipo: 'boton' };
  if (a.estado === 'analizando') return { tipo: 'analizando' };
  if (a.estado === 'error') return { tipo: 'error', motivo: a.error_analisis ?? 'desconocido', analisis: a };
  return { tipo: 'lista', analisis: a };
}

export const segmentosDe = (a: AnalisisImagen): number => a.fidelidad ?? 0;

export const colorDe = (a: AnalisisImagen): string =>
  a.decision ? COLOR_POR_ACCION[a.decision.accion] : COLOR_SIN_DECISION;

/**
 * El título del popover. Depende de la MODALIDAD porque el puntaje no mide lo mismo en las
 * dos (ADR-2026-09-13-el-analisis-sin-producto-mira-las-referencias): con producto mide
 * fidelidad al producto real; sin producto, fidelidad a lo que se pidió (el prompt y las
 * imágenes de referencia). Decirle "Fiel al producto" a una foto sin producto es decir algo
 * que nadie chequeó.
 */
export function tituloDe(a: AnalisisImagen, modalidad: ModalidadVerificacion | null = 'producto'): string {
  const accion = a.decision?.accion;
  if (accion === 'pedir_fotos') return 'Faltan fotos del producto';
  if (accion === 'regenerar') return 'Conviene regenerar';
  if (modalidad === 'referencias') {
    return a.fidelidad === 4 ? 'Hace lo que pediste' : 'Bien, con desvíos menores';
  }
  if (a.fidelidad === 4) return 'Fiel al producto';
  return 'Bien, con diferencias menores';
}

/**
 * ¿El análisis pide REHACER la imagen? Es el mismo flag que usa el harness del test masivo
 * para pintar sus badges (`decision.accion`), y acá decide cuánto peso visual lleva el botón
 * de regenerar: azul cuando el análisis lo pide, blanco cuando la imagen ya está bien
 * (ADR-2026-09-07-verificacion-de-calidad-en-la-columna).
 *
 * `pedir_fotos` YA NO cuenta como sugerencia de regenerar
 * (supersede ADR-2026-09-07-verificacion-de-calidad-en-la-columna en este punto): ahí el
 * problema está en las fotos cargadas y volver a generar gasta un crédito para chocarse con
 * la misma referencia. El azul se lo lleva "Cargar más fotos"
 * (ADR-2026-09-08-cargar-fotos-en-vez-de-regenerar).
 */
export const sugiereRegenerar = (a: AnalisisImagen | null | undefined): boolean =>
  a?.decision?.accion === 'regenerar';

/**
 * ¿Lo que hay que hacer es cargar más fotos del producto? El análisis encontró el problema
 * en las referencias, no en la generación: regenerar no lo arregla.
 */
export const pideFotos = (a: AnalisisImagen | null | undefined): boolean =>
  a?.decision?.accion === 'pedir_fotos';

export const esMala = (a: AnalisisImagen | null | undefined): boolean =>
  !!a && a.estado === 'lista' && a.fidelidad !== null && a.fidelidad <= UMBRAL_MALAS;

/** Las malas entre las cards VISIBLES, en el orden de la grilla. */
export function imagenesMalas(
  items: ReadonlyMap<string, ItemVerificacion>,
  visibles: readonly string[],
): string[] {
  return visibles.filter((id) => esMala(items.get(id)?.analisis));
}

const MOTIVOS: Array<[RegExp, string]> = [
  [/^Sin imágenes de producto para comparar/i, 'Este producto no tiene fotos para comparar.'],
  [/^ninguna foto de producto/i, 'Este producto no tiene fotos para comparar.'],
  [/^timeout$/i, 'Tardó demasiado. Probá de nuevo.'],
  // Modalidad sin producto: la generada se compara contra las referencias que viajaron, y
  // una que ya no está en Storage deja el análisis sin evidencia
  // (ADR-2026-09-13-el-analisis-sin-producto-mira-las-referencias).
  [/imagen de referencia ya no está en Storage/i, 'Alguna de las referencias que usaste ya no está disponible.'],
  [/no tiene contra qué compararse/i, 'Esta imagen no tiene referencias ni pedido con los que compararla.'],
];
export function traducirMotivo(m: string | null | undefined): string {
  if (m) for (const [re, texto] of MOTIVOS) if (re.test(m)) return texto;
  return 'No se pudo completar el análisis.';
}

/**
 * A qué imágenes recién generadas hay que dispararles el análisis solo
 * (ADR-2026-09-07-analisis-automatico-al-generar).
 *
 * Tres condiciones, y las tres importan:
 * - está entre las imágenes de las TANDAS de esta sesión, no del historial: prender la
 *   función no puede salir a analizar (y pagar) las mil creaciones anteriores;
 * - `/listar` ya la conoce y la da por analizable: sin fila o sin producto no hay nada
 *   que comparar, y el 400 del backend sería un error inventado por nosotros;
 * - no tiene análisis todavía ni se disparó antes en esta sesión: un reanálisis
 *   automático de lo que ya está medido es plata tirada.
 */
export function idsParaAnalisisAutomatico(p: {
  items: ReadonlyMap<string, ItemVerificacion>;
  idsDeTandas: ReadonlySet<string>;
  yaDisparados: ReadonlySet<string>;
}): string[] {
  const out: string[] = [];
  for (const id of p.idsDeTandas) {
    if (p.yaDisparados.has(id)) continue;
    const item = p.items.get(id);
    if (!item || !item.analizable || item.analisis) continue;
    out.push(id);
  }
  return out;
}

/** Los visibles que todavía no se pidieron, partidos en lotes del tamaño del endpoint. */
export function idsPorConocer(conocidos: ReadonlySet<string>, visibles: readonly string[]): string[][] {
  const nuevos = visibles.filter((id) => !conocidos.has(id));
  const lotes: string[][] = [];
  for (let i = 0; i < nuevos.length; i += LOTE_LISTAR) lotes.push(nuevos.slice(i, i + LOTE_LISTAR));
  return lotes;
}
